
import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";

interface SingleLocationMapProps {
  address: string;
  apiKey: string;
  isLoading: boolean;
  setHasError: (hasError: boolean) => void;
  width: string | number;
  height: string | number;
  showCard?: boolean;
  className?: string;
  latitude?: number;
  longitude?: number;
}

export function SingleLocationMap({
  address,
  apiKey,
  isLoading,
  setHasError,
  width,
  height,
  showCard = true,
  className = '',
  latitude,
  longitude
}: SingleLocationMapProps) {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapUrl, setMapUrl] = useState('');
  const [mapError, setMapError] = useState(false);
  
  useEffect(() => {
    // Only generate the map URL if we have an API key
    if (apiKey) {
      try {
        const url = getMapUrl(apiKey, address, latitude, longitude);
        setMapUrl(url);
        setMapError(false);
      } catch (error) {
        console.error('Error generating map URL:', error);
        setMapError(true);
        setHasError(true);
      }
    }
  }, [apiKey, address, latitude, longitude, setHasError]);
  
  const handleMapError = () => {
    console.error('Map failed to load');
    setMapError(true);
    setHasError(true);
  };

  const handleMapLoad = () => {
    setMapLoaded(true);
    setMapError(false);
  };

  const formatAddressForMapUrl = (addr: string): string => {
    // Sanitize the address to prevent injection attacks
    return encodeURIComponent(addr.replace(/[<>&"']/g, '').replace(/\s+/g, ' ').trim());
  };

  // Determine the map URL based on coordinates or address
  const getMapUrl = (key: string, addr: string, lat?: number, lng?: number): string => {
    // Validate inputs
    if (!key || ((!lat || !lng) && !addr)) {
      throw new Error('Invalid map parameters');
    }
    
    if (lat && lng) {
      return `https://www.google.com/maps/embed/v1/place?key=${key}&center=${lat},${lng}&q=${lat},${lng}&zoom=15&language=en&region=IE`;
    }
    return `https://www.google.com/maps/embed/v1/place?key=${key}&q=${formatAddressForMapUrl(addr)}&zoom=15&language=en&region=IE`;
  };

  const mapContent = (
    <div className="relative w-full h-full">
      {mapUrl && !mapError ? (
        <iframe 
          title={`Map of ${address}`}
          width="100%" 
          height="100%"
          style={{ border: 0 }} 
          loading="lazy" 
          src={mapUrl}
          allowFullScreen 
          referrerPolicy="no-referrer-when-downgrade"
          onError={handleMapError}
          onLoad={handleMapLoad}
        ></iframe>
      ) : (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-center p-4">
            <p className="text-red-500">Unable to load map</p>
          </div>
        </div>
      )}
      
      {!mapLoaded && !mapError && (
        <div className="absolute inset-0 bg-gray-100/70 flex items-center justify-center">
          <div className="text-center p-4">
            <div className="animate-spin h-8 w-8 border-2 border-grandpal-green border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-grandpal-gray">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );

  if (showCard) {
    return (
      <Card className={`overflow-hidden ${className}`} style={{ width, height }}>
        {mapContent}
      </Card>
    );
  }

  return (
    <div className={`${className}`} style={{ width, height }}>
      {mapContent}
    </div>
  );
}
